import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useEffect, useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { BrandPreview, type BrandPreviewProps } from './_components/BrandPreview';
import { CompanyFormFields } from './_components/CompanyFormFields';
import { LogoPicker } from './_components/LogoPicker';
import { OwnerCodeDialog } from './_components/OwnerCodeDialog';
import { companyFormSchema, EMPTY_COMPANY_FORM, type CompanyFormValues } from './_lib/companyForm';
import { useCreateCompany } from './_lib/useCreateCompany';
import { useCityLabel } from '../../api/geo';
import { RequirePermission } from '../../auth';
import { Routes } from '../../routes/routes';

/**
 * `/companies/new` — «Создать компанию» (AUT-403), after
 * `companies-create.html`: the form + logo picker on the left, the live
 * brand preview and the submit on the right. The sequence (create → logo →
 * owner code) is `useCreateCompany`.
 */
const useLogoPreview = (file: File | null): string | null => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) return setUrl(null);
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
};

const Aside = ({ preview, busy }: { preview: BrandPreviewProps; busy: boolean }): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('companies.preview.title')}
      </Typography>
      <BrandPreview {...preview} />
      <Typography variant="body2" color="text.secondary">
        {t('companies.form.ownerCodeNote')}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('companies.form.auditNote')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={() => navigate(Routes.App.Companies)} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={busy}
          loading={busy}
          data-testid="company-create-submit"
        >
          {t('companies.form.submit')}
        </Button>
      </Box>
    </Box>
  );
};

const CompanyCreatePageInner = (): ReactElement => {
  const { t } = useTranslation();
  const flow = useCreateCompany();
  const [logo, setLogo] = useState<File | null>(null);
  const logoUrl = useLogoPreview(logo);
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    mode: 'onTouched',
    defaultValues: EMPTY_COMPANY_FORM,
  });
  const values = form.watch();
  const cityLabel = useCityLabel(values.cityCountry, values.city, values.cityCustom);
  const { created, ownerCode } = flow;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          <RouterLink to={Routes.App.Companies}>{t('companies.title')}</RouterLink> /{' '}
          {t('companies.breadcrumbNew')}
        </Typography>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('companies.form.title')}
        </Typography>
      </Box>
      <Box
        component="form"
        noValidate
        onSubmit={form.handleSubmit((v) => flow.submit(v, logo))}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' }, gap: 2 }}
      >
        <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CompanyFormFields
            register={form.register}
            control={form.control}
            setValue={form.setValue}
            errors={form.formState.errors}
            disabled={flow.busy}
          />
          <Typography variant="subtitle2" fontWeight={700}>
            {t('companies.form.sections.logo')}
          </Typography>
          <LogoPicker
            onPick={setLogo}
            disabled={flow.busy}
            actionLabel={t('companies.logo.choose')}
            selectedName={logo?.name ?? null}
          />
        </Paper>
        <Aside preview={{ ...values, city: cityLabel, logoUrl }} busy={flow.busy} />
      </Box>
      <OwnerCodeDialog
        code={ownerCode.code}
        companyName={created?.name ?? ''}
        variant="created"
        reissuing={ownerCode.busy}
        onReissue={() => created && void ownerCode.issue(created.id)}
        onClose={flow.closeOwnerCode}
      />
    </Box>
  );
};

export const CompanyCreatePage = (): ReactElement => (
  <RequirePermission perm="verify_inspectors">
    <CompanyCreatePageInner />
  </RequirePermission>
);
